import * as React from 'react'
import Button from '@mui/lab/LoadingButton'
import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormHelperText,
} from '@mui/material'
import { useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import {
  ICreateTeamMember,
  Roles,
  TeamMember,
  RoleLabelMapping,
} from '@assembly/pages/Teams/types'
import { createTeamMember, updateTeamMember } from '@assembly/api/teamMember'
import validator from 'validator'
import ReactPhoneInput from 'react-phone-input-material-ui'
import { TextFieldPhoneInput } from '@assembly/components'

interface CreateTeamDialogTitleProps {
  children?: React.ReactNode
  onClose: () => void
}

function CreateTeamMemberDialogTitle(props: CreateTeamDialogTitleProps) {
  const { children, onClose, ...other } = props

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  )
}

interface CreateTeamMemberDialogProps {
  open: boolean
  onClose: () => void
  organizationID: string
  teamID: string
  onSuccess: (team: TeamMember) => void
  teamMember?: TeamMember
}

export default function CreateTeamMemberDialog(
  props: CreateTeamMemberDialogProps
) {
  const { open, onClose, organizationID, onSuccess, teamMember, teamID } = props
  const formInitialValues = {
    name: '',
    email: '',
    phone: '',
    role: '',
    invalid_email: false,
    invalid_name: false,
    invalid_role: false,
    invalid_phone: false,
  }
  const [formValues, setFormValues] = React.useState(formInitialValues)
  const [isSaving, setIsSaving] = React.useState<boolean>(false)
  const { addAlert } = useSnackbars()

  React.useEffect(() => {
    if (open) {
      if (teamMember) {
        setFormValues({
          ...formValues,
          ...teamMember,
        })
      }
    }
  }, [teamMember, open])

  const handleChangeFormField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target
    setFormValues({
      ...formValues,
      [name]: value,
      [`invalid_${name}`]: false,
    })
  }

  const handleClickSave = async () => {
    if (
      formValues.name.length === 0 ||
      formValues.email.length === 0 ||
      !validator.isEmail(formValues.email) ||
      formValues.role.length === 0
    ) {
      setFormValues({
        ...formValues,
        invalid_name: formValues.name.length === 0,
        invalid_email:
          formValues.email.length === 0 || !validator.isEmail(formValues.email),
        invalid_role: formValues.role.length === 0,
      })
      return
    }

    setIsSaving(true)

    const payload: ICreateTeamMember = {
      name: formValues.name,
      email: formValues.email,
      organizationID,
      primaryTeam: teamID,
      phone: formValues.phone,
      role: formValues.role,
      assemblyLines: [],
    }

    if (teamMember) {
      try {
        const { data } = await updateTeamMember({
          id: teamMember.id,
          ...payload,
        })
        setIsSaving(false)
        onSuccess(data)
        addAlert({
          message: 'Team Member Updated!',
          type: SnackbarType.Success,
        })
        handleClose()
      } catch (error: any) {
        setIsSaving(false)
        console.error(error)

        // Extract error message from API response
        let errorMessage = ''

        if (error.response && error.response.data) {
          // Try different possible error message formats
          if (error.response.data.message && typeof error.response.data.message === 'string') {
            errorMessage = error.response.data.message
          } else if (error.response.data.error && typeof error.response.data.error === 'string') {
            errorMessage = error.response.data.error
          } else if (error.response.data.detail && typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          }
        }

        // If no specific error message found, use generic error message
        if (!errorMessage) {
          if (error.message && typeof error.message === 'string') {
            errorMessage = error.message
          } else {
            errorMessage = 'An error occurred while updating the team member'
          }
        }

        addAlert({
          message: errorMessage,
          type: SnackbarType.Error,
        })
      }
    } else {
      try {
        const { data } = await createTeamMember(payload, teamID)
        setIsSaving(false)
        onSuccess(data)
        addAlert({
          message: 'Team Member Created!',
          type: SnackbarType.Success,
        })
        handleClose()
      } catch (error: any) {
        setIsSaving(false)
        console.error(error)

        // Extract error message from API response
        let errorMessage = ''

        if (error.response && error.response.data) {
          // Try different possible error message formats
          if (error.response.data.message && typeof error.response.data.message === 'string') {
            errorMessage = error.response.data.message
          } else if (error.response.data.error && typeof error.response.data.error === 'string') {
            errorMessage = error.response.data.error
          } else if (error.response.data.detail && typeof error.response.data.detail === 'string') {
            errorMessage = error.response.data.detail
          } else if (typeof error.response.data === 'string') {
            errorMessage = error.response.data
          }
        }

        // If no specific error message found, use generic error message
        if (!errorMessage) {
          if (error.message && typeof error.message === 'string') {
            errorMessage = error.message
          } else {
            errorMessage = 'An error occurred while creating the team member'
          }
        }

        addAlert({
          message: errorMessage,
          type: SnackbarType.Error,
        })
      }
    }
  }

  const handleClose = () => {
    setFormValues(formInitialValues)
    setIsSaving(false)
    onClose()
  }

  const handleChangeSelect = (event: SelectChangeEvent) => {
    const { name, value } = event.target
    setFormValues({
      ...formValues,
      [name]: value,
      [`invalid_${name}`]: false,
    })
  }

  const handleChangePhone = (value: any) => {
    setFormValues({
      ...formValues,
      phone: value,
      invalid_phone: false,
    })
  }

  return (
    <div>
      <Dialog onClose={handleClose} open={open} maxWidth="sm" fullWidth>
        <CreateTeamMemberDialogTitle onClose={handleClose}>
          {teamMember ? 'Edit' : 'Create'} Team Member
        </CreateTeamMemberDialogTitle>
        <DialogContent dividers>
          <TextField
            value={formValues.name}
            margin="dense"
            label="Name"
            type="text"
            name="name"
            fullWidth
            onChange={handleChangeFormField}
            error={formValues.invalid_name}
            helperText={formValues.invalid_name ? 'Required' : ''}
            sx={{ margin: 0, marginTop: '20px' }}
          />
          <TextField
            value={formValues.email}
            margin="dense"
            name="email"
            label="Email"
            type="email"
            fullWidth
            error={formValues.invalid_email}
            onChange={handleChangeFormField}
            helperText={
              formValues.invalid_email
                ? formValues.email.length === 0
                  ? 'Required'
                  : 'Invalid email'
                : ''
            }
            sx={{ margin: 0, marginTop: '20px' }}
          />
          <Box marginTop="20px">
            <ReactPhoneInput
              value={formValues.phone}
              onChange={handleChangePhone}
              component={TextFieldPhoneInput}
              country={'us'}
            />
          </Box>
          <Box marginTop="20px">
            <FormControl fullWidth>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Select
                labelId="role-select-label"
                id="role-select"
                value={formValues.role}
                label="Role"
                name="role"
                onChange={handleChangeSelect}
                error={formValues.invalid_role}
              >
                {Object.values(Roles).map((value) => (
                  <MenuItem key={value} value={value}>
                    {RoleLabelMapping[value]}
                  </MenuItem>
                ))}
              </Select>
              {formValues.invalid_role && (
                <FormHelperText error={formValues.invalid_role}>
                  Required
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button loading={isSaving} onClick={handleClickSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
